import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";

import {
  PaymentChartCardDownIcon,
  PaymentChartCardMoneyIcon,
  PaymentChartCardUpIcon,
} from "../../../../assets/icons/MUI/PaymentIcons";
import { TotalPaymentProps } from "../../payment.types";

import RechartsLineChart from "./RechartsLineChart";

const TotalPayment = ({ weeklyData, lastWeek, previousWeek, total }: TotalPaymentProps) => {
  const { t } = useTranslation();

  const formattedWeeklyData = weeklyData.map((item) => ({
    day: item.day,
    [lastWeek]: item.lastWeekValue,
    [previousWeek]: item.previousWeekValue,
    currency: total?.currency || "",
  }));

  const { amount, currency, rateValue, rate } = total || {};

  return (
    <Box
      sx={{
        borderRadius: "8px",
        background: "#F7F7F9",
        display: "flex",
        padding: "20px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "32px",
        flex: "1 0 0",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: "var(--Dark---1000, #111)",
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "28px",
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <PaymentChartCardMoneyIcon />
          {t("pages.payment.paymentLinks.totalPayment")}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "60px",
        }}
      >
        {amount && !isNaN(Number(amount)) && currency!! && (
          <Typography
            sx={{
              color: "var(--Dark---1000, #111)",
              fontFamily: "Montserrat",
              fontSize: "24px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "32px",
            }}
          >
            {`${amount / 100} ${currency.toUpperCase()}`}
          </Typography>
        )}
        {!isNaN(Number(rateValue)) && Number(rateValue) !== 0 && rate!! && (
          <Typography
            sx={{
              color: rate ? "#106048" : "#FB4C61",
              fontFamily: "Montserrat",
              fontSize: "24px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "32px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {rate ? <PaymentChartCardUpIcon /> : <PaymentChartCardDownIcon />}
            {rateValue && (rateValue / 100).toPrecision(2)}
          </Typography>
        )}
      </Box>
      <Typography
        sx={{
          color: "#242236",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "14px",
          display: "block",
          textDecorationLine: "underline",
          textDecorationStyle: "dashed",
        }}
      >
        {t("pages.payment.paymentLinks.paymentOverTime")}
      </Typography>
      <RechartsLineChart
        data={formattedWeeklyData}
        lines={[
          { dataKey: lastWeek, stroke: "#106048" },
          { dataKey: previousWeek, stroke: "#9FBFB6", strokeDasharray: "5 5" },
        ]}
      />
    </Box>
  );
};

export default TotalPayment;

export const ChartSkeleton = () => {
  return (
    <Box
      sx={{
        borderRadius: "8px",
        background: "#F7F7F9",
        display: "flex",
        padding: "20px",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "32px",
        flex: "1 0 0",
      }}
    >
      {/* Header */}
      <Skeleton variant="text" width={140} height={28} />

      {/* Amount & Rate */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "60px",
        }}
      >
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="text" width={80} height={32} />
      </Box>

      {/* Subtitle */}
      <Skeleton variant="text" width={180} height={14} />

      {/* Chart Placeholder */}
      <Box sx={{ display: "flex", gap: "16px", flexDirection: "column", width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
        <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end", width: "100%" }}>
          <Skeleton variant="rectangular" width={100} height={12} />
          <Skeleton variant="rectangular" width={100} height={12} />
        </Box>
      </Box>
    </Box>
  );
};
